import React from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Link } from 'react-router-dom';
import { makeStyles, Theme } from '@material-ui/core';
import { serverConfig } from "../../utils/config";

const useStyles = makeStyles((theme: Theme) => ({
  adCarouselContainer: {
    margin: theme.spacing(4, 0),
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    position: 'relative',
    
    '& .slick-prev': {
      left: theme.spacing(2),
      zIndex: 1,
      [theme.breakpoints.down('sm')]: {
        left: theme.spacing(1),
      },
    },
    '& .slick-next': {
      right: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        right: theme.spacing(1),
      },
    },
    '& .slick-prev:before, & .slick-next:before': {
      color: theme.palette.primary.main,
      fontSize: theme.typography.pxToRem(24),
    },
    '& .slick-dots': {
      bottom: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        bottom: theme.spacing(1),
      },
    },
    '& .slick-dots li button:before': {
      color: theme.palette.common.white,
      opacity: 0.5,
      fontSize: theme.typography.pxToRem(10),
    },
    '& .slick-dots li.slick-active button:before': {
      color: theme.palette.primary.main,
      opacity: 1,
    },
  },
  slideContainer: {
    height: 300,
    [theme.breakpoints.up('md')]: {
      height: 400,
    },
  },
  adImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.02)',
    },
  },
}));

interface AdPicturesCarouselProps {
  photos: Array<{
    fileURL: string;
    filename?: string;
  }>;
  adUrl?: string;
}


export function AdPicturesCarousel({ photos, adUrl }: AdPicturesCarouselProps) {
  const classes = useStyles();
  const Env = serverConfig;
  
  if (!photos || photos.length === 0) return null;

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: true,
    adaptiveHeight: true,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 600,
        settings: {
          arrows: false,
        }
      }
    ]
  };

  const handleAdClick = (e: React.MouseEvent, url?: string) => {
    if (!url) return;
    
    // Check if the URL is absolute (starts with http:// or https://)
    if (/^https?:\/\//.test(url)) {
      e.preventDefault();
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    // If it's not an absolute URL, let the Link component handle it as an internal route
  };

  return (
    <div className={classes.adCarouselContainer}>
      <Slider {...settings}>
        {photos.map((photo, index) => (
          <div key={index} className={classes.slideContainer}>
            {adUrl ? (
              /^https?:\/\//.test(adUrl) ? (
                // For external URLs, use an anchor tag
                <a
                  href={adUrl}
                  onClick={(e) => handleAdClick(e, adUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'block', height: '100%' }}
                >
                  <img 
                    src={`${Env.ApiRoot}/images/advertising/${photo.filename}`} 
                    alt={photo.filename || `Ad ${index + 1}`}
                    className={classes.adImage}
                    loading="lazy"
                  />
                </a>
              ) : (
                // For internal routes, use Link
                <Link 
                  to={adUrl}
                  style={{ display: 'block', height: '100%' }}
                >
                  <img 
                    src={`${Env.ApiRoot}/images/advertising/${photo.filename}`} 
                    alt={photo.filename || `Ad ${index + 1}`}
                    className={classes.adImage}
                    loading="lazy"
                  />
                </Link>
              )
            ) : (
              // If no URL is provided, just show the image
              <div style={{ display: 'block', height: '100%' }}>
                <img 
                    src={`${Env.ApiRoot}/images/advertising/${photo.filename}`} 
                    alt={photo.filename || `Ad ${index + 1}`}
                    className={classes.adImage}
                    loading="lazy"
                  />
              </div>
            )}
          </div>
        ))}
      </Slider>
    </div>
  );
}